import React, {forwardRef, useEffect, useRef, useState } from 'react'
import MaterialTable from 'material-table'
import TimesheetUtilities, {performDefaultSortForTimesheetRefs} from '../common/TimesheetUtilities'

import AddBox from '@material-ui/icons/AddBox';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />)
};

// Helper function to format duration from decimal hours to "X hours and Y minutes"
const formatDuration = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) {
    return '0 minutes'
  }
  
  const totalMinutes = Math.round(decimalHours * 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  
  if (hours === 0) {
    return minutes === 1 ? '1 minute' : `${minutes} minutes`
  }
  
  if (minutes === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`
  }
  
  const hourText = hours === 1 ? '1 hour' : `${hours} hours`
  const minuteText = minutes === 1 ? '1 minute' : `${minutes} minutes`
  return `${hourText} and ${minuteText}`
}

export default function TimesheetTable(props) {

  const timesheetQueryRef = useRef(undefined)
  const timesheetUtilitiesRef = useRef(undefined)
  const [timesheetsWithRefs, setTimesheetsWithRefs] = useState(undefined)
  
  useEffect(() => {
    timesheetQueryRef.current = props.db.collection('time_entries')

    let timesheetsUnsubscribe = timesheetQueryRef.current
      .onSnapshot(querySnapshot => {
        const tempTimesheetsWithRefs = querySnapshot.docs
        timesheetUtilitiesRef.current = new TimesheetUtilities(
          props.firebase, 
          props.db, 
          tempTimesheetsWithRefs)    
        setTimesheetsWithRefs(performDefaultSortForTimesheetRefs(tempTimesheetsWithRefs))
      })
    return () => {
      timesheetsUnsubscribe()
    }
  }, [props.firebase,
    props.db]
  )

  if (!timesheetsWithRefs) {
    return <div></div>
  }

  return (
    <MaterialTable
      icons={tableIcons}
      title="Timesheets"
      columns={[
        { field: 'ref', hidden: true },
        { field: 'driverId', hidden: true },
        { title: 'Duration', field: 'duration', editable: 'never', render: rowData => formatDuration(rowData.duration)},
        { title: 'TimeIn', field: 'timeIn', 'type': 'datetime' },
        { title: 'TimeOut', field: 'timeOut', 'type': 'datetime' },
        { title: 'Type', field: 'type', lookup: { 'Driving': 'Driving', 'Break': 'Break', 'Lunch': 'Lunch' } },
      ]}
      data={timesheetUtilitiesRef.current.fetchAllForDriverForDate(props.driverId, props.selectedDate)}  
      options={{
        filtering: true,
        search: true,
        sorting: true,
        paging: false,
        addRowPosition: 'first'
      }}
      editable={{
        onRowAdd: async newData => {
          const dataWithoutRef = {...newData}
          delete dataWithoutRef.ref
          dataWithoutRef.driverId = props.driverId
          await timesheetUtilitiesRef.current.add(dataWithoutRef).catch(err => {
              alert(err)
            })
        },
        onRowUpdate: async (newData, oldData) =>{
          const dataWithoutRef = {...newData}
          delete dataWithoutRef.ref
          timesheetUtilitiesRef.current.update(oldData.ref, dataWithoutRef).catch(err => {
            alert(err)
          })
        },
      }}
    />
  )

}